import React, {useEffect, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {Box, CircularProgress, Typography} from '@mui/material'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import Button from 'src/components-new/primitives/Button'
import StatusPill from 'src/components-new/primitives/StatusPill'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {PATHS, STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import {
  buildSecondaryAssumptionResponseBody,
  followTakenAt,
  formatSecondaryAssumptionState,
  getInitialResponseForm,
  getSecondaryAssumptionStateTone,
  getAssumptionColumnOptions,
  getVisibleAssumptionFieldIds,
  isNextDoseValid,
  isResponseFormValid,
  isSecondaryAssumptionAnswered,
  SecondaryAssumption,
  SecondaryAssumptionResponseForm
} from 'src/constants/secondaryAssumptions'
import AssumptionResponseFormNew from './AssumptionResponseFormNew'
import NextDoseFieldsNew from './NextDoseFieldsNew'

// Target of the link carried by the assumption reminder (therapy-service RF2): the user arrives
// here from a mail or an SMS, often on a phone, knowing nothing but the id in the URL — not even
// which therapy it refers to, until the GET comes back. That is why this is a standalone route
// and not a view nested under /therapies: nothing here may depend on context the link cannot
// carry, and it is reachable regardless of which sidebar items the PSP has enabled.

export default function SecondaryAssumptionResponseNew(): JSX.Element {
  const {secondaryAssumptionId} = useParams<{secondaryAssumptionId: string}>()
  const toast = useToast()
  const history = useHistory()
  // where the patient lands once the dose is answered: the diary, where the answer now shows, when
  // the PSP has it; the homepage otherwise
  const diaryEnabled = useAppSelector(state => Boolean(state.pspConfigReducer.data.sidebarItems?.therapyDiary?.show))

  const [assumption, setAssumption] = useState<SecondaryAssumption | undefined>(undefined)
  const [status, setStatus] = useState<string>()
  const [statusSubmit, setStatusSubmit] = useState<string>()
  const [form, setForm] = useState<SecondaryAssumptionResponseForm>(getInitialResponseForm())
  const [hasJustAnswered, setHasJustAnswered] = useState<boolean>(false)
  // once the patient edits the next dose by hand, moving takenAt no longer drags it along
  const [isNextDoseTouched, setIsNextDoseTouched] = useState(false)

  const assumptionsColumnOptions = useAppSelector(
    state => getAssumptionColumnOptions(state.pspConfigReducer.data)
  )
  const showStatus = getVisibleAssumptionFieldIds(assumptionsColumnOptions).has('status')

  useEffect(() => {
    apiClient.getSecondaryAssumption(secondaryAssumptionId, setStatus)
      .then((data: SecondaryAssumption) => {
        setAssumption(data)
        // CA3: reopening the link — from the second channel of the same send, or later to correct
        // the answer — must show what was already answered, not an empty form.
        setForm(getInitialResponseForm(data))
        // A toast (top-right, auto-dismiss) rather than a message sitting permanently in the
        // card: it only needs to be noticed once, on arrival, not kept taking up space next to
        // the form below, which already shows the previous answer precompiled.
        if (isSecondaryAssumptionAnswered(data)) {
          toast.info('Hai già risposto a questo promemoria. Puoi correggere la risposta qui sotto.')
        }
      })
      .catch(() => setStatus(STATUS.REJECTED))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondaryAssumptionId])

  // `formOverride` lets the ✓/✗ tap submit immediately with the form it just produced, without
  // waiting for React to flush `form` state first — see AssumptionResponseFormNew's
  // `onTakenChoice`. The bottom button still calls this with no argument, reading current `form`.
  const onSubmit = async (formOverride?: SecondaryAssumptionResponseForm): Promise<void> => {
    const submitted = formOverride ?? form
    // the ✓/✗ tap submits on its own: it must not send a next dose the server would reject
    if (!isNextDoseValid(submitted, assumption)) { return }
    const body = buildSecondaryAssumptionResponseBody(submitted, assumption)
    const updated = await apiClient.patchSecondaryAssumption(secondaryAssumptionId, body, setStatusSubmit)
    if (updated) {
      // RF4.4: once moved, the new next dose is the reference - not an edit any more for the next
      // "Aggiorna" (the PATCH response does not carry nextExpectedDate)
      setAssumption((current) => ({
        ...current,
        ...updated,
        ...body.nextExpectedDate ? {nextExpectedDate: body.nextExpectedDate} : {}
      } as SecondaryAssumption))
      setHasJustAnswered(true)
      toast.success('Risposta registrata')
      history.push(diaryEnabled ? PATHS.DIARY : '/')
    }
  }

  useEffect(() => {
    if (statusSubmit === STATUS.REJECTED) {
      toast.error('Impossibile registrare la risposta. Riprova.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusSubmit])

  if (status === STATUS.PENDING) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress />
      </Box>
    )
  }

  if (status === STATUS.REJECTED || !assumption) {
    return (
      <EmptyState
        title="Promemoria non trovato"
        description="Il link potrebbe non essere più valido, oppure il promemoria non è associato al tuo profilo."
      />
    )
  }

  const wasAlreadyAnswered = isSecondaryAssumptionAnswered(assumption)

  return (
    <Box sx={{maxWidth: 640, mx: 'auto'}}>
      <Typography variant="h1" sx={{mb: 3}}>Conferma assunzione</Typography>

      <Card
        title={(
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            {assumption.drugName ?? 'Terapia accessoria'}
            {showStatus && (
              <StatusPill
                label={formatSecondaryAssumptionState(assumption.state)}
                color={getSecondaryAssumptionStateTone(assumption.state)}
              />
            )}
          </Box>
        )}
      >
        {hasJustAnswered && (
          <InlineMessage severity="success" sx={{mb: 3}}>
            Risposta registrata. Puoi modificarla anche in un secondo momento.
          </InlineMessage>
        )}

        <AssumptionResponseFormNew
          assumption={assumption}
          form={form}
          onChange={(value) => setForm(isNextDoseTouched ? value : followTakenAt(form, value))}
          disabled={statusSubmit === STATUS.PENDING}
          // Tapping ✓/✗ here already IS a complete, submittable answer — "no" needs nothing else,
          // "yes" defaults to "taken as planned" — so it submits immediately instead of also
          // requiring the button below. That button stays for the *refinement* case: changing
          // "Quando?"/parte del corpo after the fact, or reopening the link later to correct.
          onTakenChoice={(nextForm) => onSubmit(nextForm)}
          alwaysShowDetails
        />

        {/* RF3.3: shown only when the server says the next dose can be moved from this reminder */}
        {assumption.nextExpectedDate && (
          <Box sx={{mt: 3}}>
            <NextDoseFieldsNew
              value={form.nextExpectedDate ?? null}
              onChange={(value) => {
                setIsNextDoseTouched(true)
                setForm({...form, nextExpectedDate: value})
              }}
              disabled={statusSubmit === STATUS.PENDING}
              error={!isNextDoseValid(form, assumption)}
            />
          </Box>
        )}

        <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 4, flexWrap: 'wrap'}}>
          <Button
            variant="primary"
            loading={statusSubmit === STATUS.PENDING}
            disabled={!isResponseFormValid(form, assumption)}
            onClick={() => onSubmit()}
          >
            {wasAlreadyAnswered || hasJustAnswered ? 'Aggiorna' : 'Salva'}
          </Button>
        </Box>
      </Card>
    </Box>
  )
}
